import type { CalculationSettings as BlueprintCalculationSettings } from '@/pages/blueprint-lite/apis/blueprintApi';

/**
 * Flat, Purchase-specific projection of the live calculation-settings
 * payload. The result page fetches the nested
 * `useFetchCalculationSettingsQuery` shape and runs it through
 * `normaliseCalculationSettings()` before handing it to the orchestrator.
 *
 * Keeping the Purchase shape flat removes a layer of indirection at
 * every consumer site (e.g. `settings.indicativeRate` instead of
 * `settings.serviceability.baseRate`) and lets us drop blueprint-lite
 * fields we don't use (`bonusHaircut`, `rentalShade`, `rentalYield` —
 * these belong to the v1 lead-gen calc).
 */
export interface CalculationSettings {
  // Indicative rate + stress buffer for the global max-borrow calc.
  indicativeRate: number;       // serviceability.baseRate
  apraBuffer: number;           // serviceability.apraBuffer
  loanTermMonths: number;       // serviceability.loanTermMonths

  // Global income shading (non-AUD applicants).
  globalBaseShadeNonAud: number; // serviceability.globalBaseShade

  // Purchase-cost figures.
  purchaseFeesPct: number;       // serviceability.purchaseFeesPct

  // Liability monthly conversion.
  ccAssessmentFactor: number;    // liability.ccAssessmentFactor
  hecsMonthlyAud: number;        // liability.hecsMonthly

  // Investment-cashflow assumptions.
  managementFeePct: number;      // investmentCashflow.mgmtPct
  ratesInsurancePct: number;     // investmentCashflow.ratesInsPct
  maintenancePct: number;        // investmentCashflow.maintPct
  strataUnitPct: number;         // investmentCashflow.strataPct

  // Capital-gains projection growth rates.
  capitalGains5yrRate: number;   // capitalGains.cg5yrRate
  capitalGains7yrRate: number;   // capitalGains.cg7yrRate

  // Global serviceability shade for investment-property rental income
  // (v2 = 0.90). Applied in the orchestrator's adjusted-monthly-income
  // calc.
  rentalIncludeGlobal: number;   // serviceability.rentalIncludeGlobal

  // Monthly surplus below which the overall status flips green → amber.
  surplusAmberThreshold: number; // serviceability.surplusAmberThreshold
}

export function normaliseCalculationSettings(
  bp: BlueprintCalculationSettings,
): CalculationSettings {
  return {
    indicativeRate: bp.serviceability.baseRate,
    apraBuffer: bp.serviceability.apraBuffer,
    loanTermMonths: bp.serviceability.loanTermMonths,
    globalBaseShadeNonAud: bp.serviceability.globalBaseShade,
    purchaseFeesPct: bp.serviceability.purchaseFeesPct,
    ccAssessmentFactor: bp.liability.ccAssessmentFactor,
    hecsMonthlyAud: bp.liability.hecsMonthly,
    managementFeePct: bp.investmentCashflow.mgmtPct,
    ratesInsurancePct: bp.investmentCashflow.ratesInsPct,
    maintenancePct: bp.investmentCashflow.maintPct,
    strataUnitPct: bp.investmentCashflow.strataPct,
    capitalGains5yrRate: bp.capitalGains.cg5yrRate,
    capitalGains7yrRate: bp.capitalGains.cg7yrRate,
    rentalIncludeGlobal: bp.serviceability.rentalIncludeGlobal,
    surplusAmberThreshold: bp.serviceability.surplusAmberThreshold,
  };
}
